import { apiMutate } from '../admin/primitives.js'
import { entrySequence, type MotionLevel, type NavKind } from './entry-motion.js'

/**
 * SIGNATURE MOTION connexion / déconnexion (LOT 5.5.1).
 *
 * Deux séquences orchestrées en CSS (voir auth.css / shell.css), déclenchées
 * par des classes sur <html> — uniquement transform / opacity / filter,
 * jamais de layout :
 *
 *   CONNEXION   `lc-auth-leave` sur l'écran de login (halo du CTA qui se
 *               propage, particules qui convergent, carte qui se dissout,
 *               sweep lumineux) → chargement du shell → `lc-entering`
 *               (sidebar et contenu se révèlent en profondeur).
 *   DÉCONNEXION la session est INVALIDÉE CÔTÉ SERVEUR d'abord — l'animation
 *               n'est jamais un rideau devant une session encore vivante.
 *               Puis `lc-leaving` : le contenu recule, les lumières
 *               s'éteignent, le fond Auth reprend.
 *
 * Respect du mouvement : réduit → fondu bref ; minimal → quasi instantané.
 * La transition ne MASQUE jamais une attente : elle ne démarre qu'après le
 * succès réel de l'opération d'authentification.
 */

const ENTRY_FLAG = 'gestion.entry-motion'


export function motionLevel(): MotionLevel {
  const r = document.documentElement
  const pref = r.getAttribute('data-motion') ?? 'system'
  if (pref === 'minimal') return 'minimal'
  if (pref === 'reduced') return 'reduced'
  if (pref === 'system' && window.matchMedia('(prefers-reduced-motion: reduce)').matches) return 'reduced'
  return 'full'
}

const wait = (ms: number) => new Promise<void>((r) => setTimeout(r, ms))

/** Après un LOGIN réussi (état AUTHENTICATED confirmé par le serveur) :
 *  joue la sortie signature puis navigue vers la destination. */
export async function runLoginSuccess(destination: string): Promise<void> {
  const level = motionLevel()
  try { sessionStorage.setItem(ENTRY_FLAG, level) } catch { /* stockage indisponible */ }
  if (level !== 'minimal') {
    document.documentElement.classList.add(level === 'full' ? 'lc-auth-leave' : 'lc-auth-leave-soft')
    await wait(level === 'full' ? 880 : 180)
  }
  window.location.assign(destination)
}

/* ══════════════════════ ENTRÉE DANS L'APPLICATION ══════════════════════
 *
 * Ouvrir directement gestion.liveclubrennes.com avec une session valide
 * n'était PAS une entrée : le shell apparaissait d'un coup, sans transition,
 * alors que la même arrivée après connexion avait droit à une séquence
 * complète. Ce sont pourtant les deux mêmes instants du produit.
 *
 * Trois principes tiennent cette séquence :
 *
 *  · ELLE NE SIMULE RIEN. Aucun formulaire, aucun « Connexion… », aucune
 *    attente ajoutée : l'utilisateur EST connecté, on ne lui rejoue pas son
 *    authentification.
 *  · ELLE NE PART PAS AVANT DE SAVOIR. Tant que le serveur n'a pas dit qui
 *    est là, le shell reste retenu — annoncer « application prête » puis
 *    rediriger vers /login serait un mensonge de 400 ms.
 *  · ELLE NE PEUT PAS RETENIR LE PRODUIT. Un minuteur de sécurité révèle
 *    l'application quoi qu'il arrive : une animation n'a jamais le droit de
 *    cacher Gestion parce qu'un appel réseau s'éternise.
 */

const BOOT_CLASS = 'lc-boot'
let revele = false
let filet: number | null = null

/** Type de navigation du DOCUMENT — sert à doser l'entrée (§13, §14). */
function typeDeNavigation(): NavKind {
  try {
    const e = performance.getEntriesByType('navigation')[0] as PerformanceNavigationTiming | undefined
    const t = e?.type
    if (t === 'navigate' || t === 'reload' || t === 'back_forward') return t
  } catch { /* API indisponible */ }
  return 'other'
}


/**
 * AVANT le premier rendu : retenir le shell. Seulement le shell — le fond
 * LIVE CONTROL, lui, est peint tout de suite (§11 : aucun flash blanc, aucune
 * coquille non stylée). À n'appeler que hors parcours d'authentification.
 */
export function bootAppEntry(): void {
  if (motionLevel() === 'minimal' || typeDeNavigation() === 'back_forward') { revele = true; return }
  document.documentElement.classList.add(BOOT_CLASS)
  filet = window.setTimeout(() => enterApp(), 1500)
}

/**
 * À l'arrivée dans le shell, une fois la session CONNUE : joue l'entrée qui
 * convient. Idempotente — appelée depuis plusieurs branches d'AppShell.
 *
 *   depuis /login  → séquence signature complète (inchangée)
 *   ouverture      → entrée application : la marque, le rail, le contenu
 *   rechargement   → version courte (§14)
 *   retour arrière → rien
 */
export function enterApp(): void {
  if (revele) return
  revele = true
  if (filet !== null) { clearTimeout(filet); filet = null }
  const r = document.documentElement
  r.classList.remove(BOOT_CLASS)

  let depuisLogin: string | null = null
  try {
    depuisLogin = sessionStorage.getItem(ENTRY_FLAG)
    sessionStorage.removeItem(ENTRY_FLAG)
  } catch { /* stockage indisponible */ }

  const { classe, duree } = entrySequence({
    fromLogin: depuisLogin, level: motionLevel(), nav: typeDeNavigation(),
  })
  if (!classe) return
  r.classList.add(classe)
  setTimeout(() => { r.classList.remove('lc-entering', 'lc-entering-soft', 'lc-booting') }, duree)
}

/** DÉCONNEXION : révocation serveur D'ABORD, animation ensuite. */
export async function runLogout(): Promise<void> {
  await apiMutate('POST', '/api/v1/auth/logout')
  const level = motionLevel()
  if (level !== 'minimal') {
    document.documentElement.classList.add(level === 'full' ? 'lc-leaving' : 'lc-leaving-soft')
    await wait(level === 'full' ? 780 : 180)
  }
  window.location.assign('/login')
}
